import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FormProvider, useFieldArray, useForm, get} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faMinus, faPlus} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Utils
import {getFields, replacePseudoFields} from "../../utils/builder.js";
import {
  descendDependencies,
  getAncestryName,
  isWeatherConditionDepends,
  normalizeWeatherCompareValue,
  WEATHER_CHECK_OPTIONS,
  WEATHER_DEPENDS_OPTIONS
} from "./helpers.js";

// Components
import SearchSelect from "../../components/SearchSelect.js";
import Dropdown from "../../components/Dropdown.js";
import {
  InputSelect,
  InputRadioGroup,
  InputDate,
  InputDay,
  InputText,
  InputTime,
  InputError
} from "../../components/form/FormInputs.js";

// Style
import {cross} from "../../style/components/shapes.js";
import {pad, radius} from "../../style/components/variables.js";
import {
  Button,
  Form,
  FormGroup,
  FormField,
  Inline,
  NotLoaded,
  Loader
} from "../../style/components/general.js";
import {flex} from "../../style/components/mixins.js";

const initialConditional = watchTrigger => ({
  check: watchTrigger === "parentDisabled" ? "===" : "",
  compare: ""
});

const defaultValues = {
  trigger: "date",
  condition: {
    depends: "date",
    conditionArray: [{...initialConditional("date")}],
    operator: "or"
  }
};

const CIRCULAR_MESSAGE =
  "This would create a loop: the selected field already depends on this field through another condition. Update that condition, or choose a different field.";

const ManageConditional = ({
  builder,
  submission,
  existing,
  setExisting,
  open,
  canCancel = true,
  targetElement,
  hasPrimaryAddress = false,
  fields,
  allowedTriggers,
  weatherDependsOptions = WEATHER_DEPENDS_OPTIONS
}) => {
  const {allIds, byId} = builder;

  const initialized = useRef(false);

  const [results, setResults] = useState([]);

  const [dependsDropdownVal, setDependsDropdownVal] = useState();

  const schema = yup.object().shape({
    trigger: yup.string().nullable().required(),
    condition: yup.object().shape({
      depends: yup
        .string()
        .required("This field is required.")
        .test({
          test: val =>
            fields
              ? fields.every(field => descendDependencies(val, builder, [field]))
              : descendDependencies(val, builder, [targetElement?.name]),
          message: CIRCULAR_MESSAGE
        }),
      conditionArray: yup.lazy((_v, ctx) => {
        if (ctx.parent.depends === "weekday")
          return yup.array().of(
            yup.object().shape({
              check: yup.mixed().nullable(),
              compare: yup.array().of(yup.string()).required("This field is required.")
            })
          );
        if (ctx.parent.depends === "date")
          return yup.array().of(
            yup.object().shape({
              check: yup.mixed().nullable(),
              compare: yup.string().required("This field is required.")
            })
          );
        return yup.array().of(
          yup.object().shape({
            check: yup.string().required("This field is required."),
            compare: yup.string().required("This field is required.")
          })
        );
      }),
      operator: yup.string().nullable()
    })
  });

  const form = useForm({
    // we need to generate the options for given dependency before setting the default values if editing
    defaultValues: existing || defaultValues,
    resolver: yupResolver(schema)
  });
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: {errors, submitCount}
  } = form;
  const {append, remove} = useFieldArray({
    control,
    name: "condition.conditionArray"
  });
  const watchTrigger = watch("trigger");
  const watchDepends = watch("condition.depends");
  const watchConditionArray = watch("condition.conditionArray");

  const parentElement = useMemo(() => {
    if (fields) {
      const [first] = fields;
      const {parentName} = byId[first] || {};
      if (parentName && fields.every(field => byId[field]?.parentName === parentName))
        return byId[parentName];
      return null;
    }
    if (targetElement?.parentName && byId[targetElement.parentName])
      return byId[targetElement.parentName];
    return null;
  }, [targetElement, byId, fields]);

  useEffect(() => {
    // Depends is same as trigger if not a field condition
    if (!["field", "weather"].includes(watchTrigger) && initialized.current)
      setValue("condition.depends", watchTrigger, {shouldValidate: !!submitCount});
    else if (initialized.current && watchTrigger !== "parentDisabled")
      setValue("condition.depends", null, {shouldValidate: !!submitCount});

    // reset on change
    if (watchTrigger && initialized.current)
      setValue("condition.conditionArray", [{...initialConditional(watchTrigger)}], {
        shouldValidate: !!submitCount
      });

    if (watchTrigger === "parentDisabled")
      setValue("condition.depends", parentElement?.name, {shouldValidate: !!submitCount});

    if (!initialized.current) initialized.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchTrigger, setValue, parentElement]);

  useEffect(() => {
    if (existing && (watchTrigger === "field" || watchTrigger === "weather")) {
      setDependsDropdownVal(existing.condition?.depends || "");
    }
  }, [existing, watchTrigger]);

  const getAllOptions = useCallback(() => {
    if (fields) {
      const tempFields = getFields(allIds, byId).filter(
        ({name, stale}) => !fields.includes(name) && !stale
      );

      const tempSiblings = tempFields
        .filter(
          el => parentElement && el.parentName === parentElement?.name && el.type !== "weather"
        )
        .map(({name, ancestry: label}) => ({name, label}));
      const external = tempFields
        .filter(
          el => (!parentElement || el.parentName !== parentElement?.name) && el.type !== "weather"
        )
        .map(({name, ancestry: label}) => ({name, label}));

      return [...tempSiblings, ...external];
    }

    const tempFields = getFields(allIds, byId).filter(
      ({name, stale}) => name !== targetElement?.name && !stale
    );

    const tempSiblings = tempFields
      .filter(el => el.parentName === targetElement?.parentName && el.type !== "weather")
      .map(({name, ancestry: label}) => ({name, label}));
    const external = tempFields
      .filter(el => el.parentName !== targetElement?.parentName && el.type !== "weather")
      .map(({name, ancestry: label}) => ({name, label}));

    return [...tempSiblings, ...external];
  }, [allIds, byId, targetElement, fields, parentElement]);

  const getDropdownOptions = useCallback(() => {
    if (fields) {
      const tempFields = getFields(allIds, byId).filter(
        ({name, stale}) => !fields.includes(name) && !stale
      );

      const tempSiblings = tempFields
        .filter(
          el => parentElement && el.parentName === parentElement?.name && el.type !== "weather"
        )
        .map(({name, ancestry: label}) => ({name, label}));
      const external = tempFields
        .filter(
          el => (!parentElement || el.parentName !== parentElement?.name) && el.type !== "weather"
        )
        .map(({name, ancestry: label}) => ({name, label}));

      if (external.length && tempSiblings.length)
        return [...tempSiblings, {label: "Other field", name: "external"}];
      if (external.length) return [];

      return [...tempSiblings];
    }

    const tempFields = getFields(allIds, byId).filter(
      ({name, stale}) => name !== targetElement?.name && !stale
    );

    const tempSiblings = tempFields
      .filter(el => el.parentName === targetElement?.parentName && el.type !== "weather")
      .map(({name, ancestry: label}) => ({name, label}));
    const external = tempFields
      .filter(el => el.parentName !== targetElement?.parentName && el.type !== "weather")
      .map(({name, ancestry: label}) => ({name, label}));

    if (external.length) return [...tempSiblings, {label: "Other field", name: "external"}];
    if (external.length) return [];

    return [...tempSiblings];
  }, [allIds, byId, targetElement, fields, parentElement]);

  const getCheckOptions = useCallback(() => {
    const depends = watchDepends ?? existing?.condition?.depends;
    let check = [];
    if (depends && watchTrigger !== "parentDisabled") {
      const {sanitized: sanitizedDepends} = replacePseudoFields(watchDepends);
      if (sanitizedDepends && byId[sanitizedDepends]) {
        const {type} = byId[sanitizedDepends];
        check = [
          {label: "is", name: "==="},
          {label: "is not", name: "!=="}
        ];

        if ((type && type === "number") || type === "generated" || type === "range")
          check = check.concat([
            {label: "is greater than", name: ">"},
            {label: "is less than", name: "<"},
            {label: "is greater than or equal to", name: ">="},
            {label: "is less than or equal to", name: "<="}
          ]);

        if (type && type === "multiselect") check = [{label: "has", name: "contains"}];

        if (type && type === "text") check.push({label: "contains", name: "contains"});

        if (type && ["time", "date"].includes(type))
          check = check.concat([
            {label: "is after", name: ">"},
            {label: "is before", name: "<"}
          ]);
      } else if (isWeatherConditionDepends(depends)) {
        check = WEATHER_CHECK_OPTIONS;
      }
    } else if (watchTrigger === "parentDisabled") check.push({label: "is", name: "==="});

    return check;
  }, [byId, existing, watchDepends, watchTrigger]);

  const getAllowOther = useCallback(() => {
    const depends = watchDepends ?? existing?.condition?.depends;
    if (depends && watchTrigger !== "parentDisabled") {
      if (isWeatherConditionDepends(depends)) return false;
      const {sanitized: sanitizedDepends} = replacePseudoFields(watchDepends);
      if (sanitizedDepends && byId[sanitizedDepends]) {
        const {type} = byId[sanitizedDepends];

        const noOther = [
          "checkbox",
          "confirm",
          "radio",
          "dropdown",
          "switch",
          "multiselect",
          "upload"
        ];

        if (!type || noOther.includes(type)) return false;

        return true;
      }
    }
    return false;
  }, [byId, existing, watchDepends, watchTrigger]);

  const getCompareOptions = useCallback(() => {
    const depends = watchDepends ?? existing?.condition?.depends;
    let compare = [];
    if (depends && watchTrigger !== "parentDisabled") {
      if (isWeatherConditionDepends(depends)) return [];
      const {sanitized: sanitizedDepends} = replacePseudoFields(watchDepends);
      if (sanitizedDepends && byId[sanitizedDepends]) {
        const {type, options, on, off} = byId[sanitizedDepends];

        if (type === "checkbox" || type === "confirm")
          compare = [
            {label: "True", name: "true"},
            {label: "False", name: "false"}
          ];

        if (type === "radio" || type === "dropdown")
          compare = options.map(select => ({
            label: select.value || select.option || select,
            name: select.value || select.option || select
          }));

        if (type === "switch")
          compare = [
            {label: on, name: on},
            {label: off, name: off}
          ];

        if (type === "number")
          compare = [
            {label: "0", name: "0"},
            {label: "1", name: "1"}
          ];

        if (type === "multiselect")
          compare = [
            {label: "all checked", name: "checked"},
            {label: "all unchecked", name: "unchecked"},
            {label: "one checked", name: "one"},
            {label: "at least one checked", name: "more"},
            ...options.map(select => ({
              label: `"${select.value || select.option || select}" selected`,
              name: select.value || select.option || select
            }))
          ];

        if (type === "upload") compare = [{label: "uploaded", name: "uploaded"}];
      }
    } else if (watchTrigger === "parentDisabled") {
      compare = [
        {label: parentElement?.disabledLabel || "Out of Service", name: "disabled"},
        {label: parentElement?.enabledLabel || "In Service", name: "enabled"}
      ];
    }
    return compare;
  }, [byId, existing, watchDepends, parentElement, watchTrigger]);

  const getRadioOptions = useCallback(() => {
    const options = [
      {label: "Date", value: "date"},
      {label: "Weekday", value: "weekday"}
    ];

    if (hasPrimaryAddress) options.push({label: "Weather", value: "weather"});

    if (parentElement?.allowDisable)
      options.push({
        label: `Component ${parentElement.disabledLabel || "Out of Service"}`,
        value: "parentDisabled"
      });

    options.push({label: "Field", value: "field"});

    if (!allowedTriggers?.length) return options;
    return options.filter(({value}) => allowedTriggers.includes(value));
  }, [hasPrimaryAddress, parentElement, allowedTriggers]);

  const radioOptions = useMemo(getRadioOptions, [getRadioOptions]);
  const allOptions = useMemo(getAllOptions, [getAllOptions]);
  const dropdownOptions = useMemo(getDropdownOptions, [getDropdownOptions]);

  const search = query => {
    if (query) {
      const lower = query.toLowerCase();
      const tempResults = allOptions.filter(option => option.label.toLowerCase().includes(lower));
      tempResults.sort(
        (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
      );
      setResults(tempResults);
    } else setResults(allOptions);
  };

  const handleAdd = ({trigger, condition}) => {
    const nextCondition = {...condition};
    if (nextCondition.conditionArray?.length && isWeatherConditionDepends(nextCondition.depends)) {
      nextCondition.conditionArray = nextCondition.conditionArray.map(row => ({
        ...row,
        compare: normalizeWeatherCompareValue(row.compare)
      }));
    }
    submission({trigger, ...nextCondition});

    // Handle reset
    reset(defaultValues);
    open(false);
  };

  const checkOptions = useMemo(getCheckOptions, [getCheckOptions]);
  const compareOptions = useMemo(getCompareOptions, [getCompareOptions]);
  const allowOther = useMemo(getAllowOther, [getAllowOther]);
  const isWeatherDepends = isWeatherConditionDepends(watchDepends);

  useEffect(() => {
    if (!isWeatherDepends || !watchConditionArray?.length) return;
    watchConditionArray.forEach((row, i) => {
      const normalized = normalizeWeatherCompareValue(row?.compare);
      if (normalized !== row?.compare)
        setValue(`condition.conditionArray.${i}.compare`, normalized);
    });
  }, [isWeatherDepends, watchConditionArray, setValue]);

  const weatherDependsLabel = useMemo(() => {
    const dependsKey =
      watchDepends === "precipitationchance" ? "precipitationChance" : watchDepends;
    return weatherDependsOptions.find(({name}) => name === dependsKey)?.label;
  }, [watchDepends, weatherDependsOptions]);

  const dependsLabel = useMemo(() => {
    if (watchTrigger === "parentDisabled") return parentElement?.label;
    if (weatherDependsLabel) return weatherDependsLabel;
    const element = byId[watchDepends];
    if (!element) return "";
    return getAncestryName(element, {byId});
  }, [watchDepends, watchTrigger, byId, parentElement, weatherDependsLabel]);

  const circularError = get(errors, "condition.depends")?.message === CIRCULAR_MESSAGE;

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(handleAdd)} noValidate>
        <FormField>
          <InputRadioGroup
            testId="condition.trigger"
            name="trigger"
            label="Trigger on..."
            options={radioOptions}
            disabled={!!existing}
          />
        </FormField>
        <Columns>
          {["field", "weather"].includes(watchTrigger) && (
            <ColLeft hasMinWidth={!dependsLabel}>
              <InputText name="condition.depends" hidden required />
              {!dependsLabel ? (
                <DependsWrapper>
                  {watchTrigger === "field" &&
                    dependsDropdownVal !== "external" &&
                    !!dropdownOptions?.length && (
                      <Dropdown
                        testId="condition.fieldSelect"
                        options={dropdownOptions}
                        showPlaceholder
                        placeholder="Select..."
                        selection={dependsDropdownVal}
                        setSelection={val => {
                          if (val !== "external")
                            setValue("condition.depends", val, {shouldValidate: !!submitCount});
                          setDependsDropdownVal(val);
                        }}
                      />
                    )}
                  {watchTrigger === "weather" && dependsDropdownVal !== "external" && (
                    <Dropdown
                      testId="condition.weatherSelect"
                      noSanitize
                      options={weatherDependsOptions}
                      placeholder="Select..."
                      selection={dependsDropdownVal || watchDepends || ""}
                      setSelection={val => {
                        setValue("condition.depends", val, {shouldValidate: !!submitCount});
                        setDependsDropdownVal(val);
                      }}
                      showPlaceholder
                    />
                  )}
                  {watchTrigger === "field" &&
                    (dependsDropdownVal === "external" || !dropdownOptions?.length) && (
                      <FieldDropdown>
                        <SearchSelect
                          results={results}
                          setResults={setResults}
                          search={search}
                          add={item =>
                            setValue("condition.depends", item.name, {
                              shouldValidate: !!submitCount
                            })
                          }
                          showAll
                        />
                        {dropdownOptions?.length > 0 && (
                          <DeleteButton
                            onClick={() => {
                              setValue("condition.depends", "", {shouldValidate: !!submitCount});
                              setDependsDropdownVal("");
                            }}
                            type="button">
                            <IconWrapper>
                              <CloseIcon data-testid="formula.cancelSearch" />
                            </IconWrapper>
                          </DeleteButton>
                        )}
                      </FieldDropdown>
                    )}
                </DependsWrapper>
              ) : (
                <Depends
                  type="button"
                  title={dependsLabel}
                  onClick={() => {
                    setValue("condition.depends", "", {shouldValidate: !!submitCount});
                    setDependsDropdownVal("");
                  }}>
                  <span>{dependsLabel}</span>
                  {watchTrigger !== "parentDisabled" && (
                    <>
                      &nbsp;&nbsp;
                      <FontAwesomeIcon icon={faClose} />
                    </>
                  )}
                </Depends>
              )}
              {!circularError && <InputError name="condition.depends" errors={errors} />}
            </ColLeft>
          )}
          <ColRight>
            {watchConditionArray ? (
              watchConditionArray.map((_f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <SpacedWrapper key={`compareArray-${i}`} fullWidth>
                  {watchDepends === "date" && (
                    <>
                      <FormField inline>
                        <InputDate name={`condition.conditionArray.${i}.compare`} />
                      </FormField>
                      <FormField inline>
                        <InputSelect
                          name={`condition.conditionArray.${i}.frequency`}
                          placeholder="Does not repeat..."
                          options={["weekly", "monthly", "annually"]}
                          minWidth={120}
                          required
                        />
                      </FormField>
                      {watch(`condition.conditionArray.${i}.frequency`) === "monthly" && (
                        <FormField inline>
                          <LastDay
                            type="button"
                            onClick={() => {
                              setValue(
                                `condition.conditionArray.${i}.compare`,
                                dayjs().endOf("month").format("YYYY-MM-DD"),
                                {shouldValidate: !!submitCount}
                              );
                              setValue(
                                `condition.conditionArray.${i}.lastDay`,
                                !watch(`condition.conditionArray.${i}.lastDay`)
                              );
                            }}
                            enabled={watch(`condition.conditionArray.${i}.lastDay`)}>
                            Last Day of Month
                          </LastDay>
                        </FormField>
                      )}
                    </>
                  )}
                  {watchTrigger === "parentDisabled" && (
                    <>
                      <InputSelect
                        testId="condition.check"
                        name={`condition.conditionArray.${i}.check`}
                        placeholder="Condition..."
                        options={checkOptions}
                        minWidth={120}
                        disabled
                        required
                        defaultValue="==="
                      />
                      <InputSelect
                        testId="condition.compare"
                        name={`condition.conditionArray.${i}.compare`}
                        placeholder="Compare to..."
                        options={compareOptions}
                        minWidth={120}
                        required
                      />
                    </>
                  )}
                  {watchDepends === "weekday" && (
                    <FormField>
                      <InputDay testId="day-input" name={`condition.conditionArray.${i}.compare`} />
                    </FormField>
                  )}
                  {((watchTrigger === "field" && watchDepends && byId[watchDepends]) ||
                    isWeatherConditionDepends(watchDepends)) && (
                    <>
                      <InputSelect
                        testId="condition.check"
                        name={`condition.conditionArray.${i}.check`}
                        placeholder="Condition..."
                        options={checkOptions}
                        minWidth={120}
                        disabled={!checkOptions || !dependsLabel}
                        required
                      />
                      {byId[watchDepends]?.type === "time" && (
                        <InputTime
                          testId="condition.compare"
                          name={`condition.conditionArray.${i}.compare`}
                          placeholder="Compare to..."
                        />
                      )}
                      {byId[watchDepends]?.type === "date" && (
                        <InputDate
                          testId="condition.compare"
                          name={`condition.conditionArray.${i}.compare`}
                        />
                      )}
                      {!["time", "date"].includes(byId[watchDepends]?.type) &&
                        (isWeatherDepends ? (
                          <InputText
                            testId="condition.compare"
                            name={`condition.conditionArray.${i}.compare`}
                            placeholder="Value..."
                            disabled={!watch(`condition.conditionArray.${i}.check`)}
                            required
                          />
                        ) : (
                          <InputSelect
                            testId="condition.compare"
                            name={`condition.conditionArray.${i}.compare`}
                            placeholder="Compare to..."
                            options={compareOptions}
                            other={allowOther}
                            minWidth={120}
                            disabled={
                              !compareOptions || !watch(`condition.conditionArray.${i}.check`)
                            }
                            required
                          />
                        ))}
                    </>
                  )}
                  {watchConditionArray.length > 1 &&
                    watchDepends &&
                    watchDepends !== "weekday" &&
                    watchTrigger !== "parentDisabled" && (
                      <Button
                        data-testid="condition.remove"
                        type="button"
                        onClick={() => remove(i)}>
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                    )}
                  {watchConditionArray.length < 5 &&
                    watchDepends &&
                    watchDepends !== "weekday" &&
                    watchTrigger !== "parentDisabled" && (
                      <Button
                        data-testid="condition.add"
                        type="button"
                        onClick={() => append(initialConditional(watchTrigger))}>
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    )}
                </SpacedWrapper>
              ))
            ) : (
              <NotLoaded>
                <Loader />
              </NotLoaded>
            )}
          </ColRight>
        </Columns>

        {watchDepends !== "weekday" && watchConditionArray && watchConditionArray.length > 1 && (
          <FormField>
            <InputRadioGroup
              name="condition.operator"
              label="Logical Operator"
              options={[{option: "or"}, {option: "and"}]}
              testId="condition.operator"
              defaultValue="or"
            />
          </FormField>
        )}

        <SpacedWrapper hasBottomMargin={circularError}>
          <Button type="submit" data-testid="condition.stage">
            {existing ? "Update" : "Add"}
          </Button>
          {open && setExisting && canCancel && (
            <Button
              type="button"
              onClick={() => {
                reset(defaultValues);
                setExisting(null);
                open(false);
              }}>
              Cancel
            </Button>
          )}
        </SpacedWrapper>
        {circularError && (
          <BottomError>
            <InputError name="condition.depends" errors={errors} />
          </BottomError>
        )}
      </Form>
    </FormProvider>
  );
};

ManageConditional.propTypes = {
  submission: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  existing: PropTypes.objectOf(PropTypes.any),
  setExisting: PropTypes.func,
  allOptions: PropTypes.arrayOf(PropTypes.any),
  open: PropTypes.func,
  canCancel: PropTypes.bool,
  targetElement: PropTypes.objectOf(PropTypes.any),
  hasPrimaryAddress: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.string),
  allowedTriggers: PropTypes.arrayOf(PropTypes.string),
  weatherDependsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

// Style Overrides
const Columns = styled(Inline)`
  width: 100%;
  margin-bottom: ${pad * 2}px;
`;

const Col = styled(FormGroup)`
  align-self: center;
  margin: 0;
`;

const ColLeft = styled(Col)`
  align-self: start;
  width: max-content;
  max-width: 50%;

  ${({hasMinWidth}) =>
    hasMinWidth &&
    css`
      min-width: 150px;

      div {
        max-width: unset;
        width: 100%;
      }
    `}
`;

const ColRight = styled(Col)`
  max-width: 50%;
`;

const Depends = styled(Button)`
  display: flex;
  align-items: center;

  span {
    max-width: 200px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const SpacedWrapper = styled(Inline)`
  gap: ${pad}px;
  margin-bottom: ${pad}px;
  align-items: start;

  ${({hasBottomMargin}) =>
    hasBottomMargin &&
    css`
      margin-bottom: 30px;
    `}

  div {
    margin-top: 0;
  }
`;

const DependsWrapper = styled(Inline)`
  max-width: max-content;
`;

const FieldDropdown = styled.div`
  margin: 0;
  width: 180px;
  height: min-content;
  border-radius: ${radius};
  ${flex("row", "nowrap")}
  align-items: center;
`;

const DeleteButton = styled(Button)`
  background-color: transparent;
  padding: ${pad / 2}px ${pad / 4}px;
`;

const CloseIcon = styled.div`
  ${({theme}) => cross("100%", theme.secondary)};
  margin: auto ${pad}px;
`;

const IconWrapper = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
`;

const LastDay = styled(Button)`
  ${({enabled}) => css`
    opacity: ${enabled ? 1 : 0.5};
  `}
`;

const BottomError = styled.div`
  margin-top: -${pad}px;

  > span {
    margin-top: -${pad * 2}px;
  }
`;

export default ManageConditional;
